import download from "downloadjs";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import moment from "moment";
import _ from "lodash";
import { buildOrderWarrantyTexts } from "../../utils/buildOrderWarranties";
import { formatAwningSupplementaryValue } from "../../constants/awningSupplementaryOptions";
import {
    getInvoiceSalesBreakdown,
    resolveInvoiceTotalAmount,
} from "../../utils/invoiceSalesBreakdown";
import { publicRequest } from "../../requestMethods";
import { IPOH_ACCOUNT_ID } from "../../utils/ipohAccount";
import { PAYMENT_SLOTS, paymentAmountField } from "../../constants/paymentSlots";

const IPOH_INVOICE_HEADER = {
    name: "INHOME AWNING SPECIALIST (IPOH) SDN BHD",
    addressLines: [
        "NO 19, MENGLEMBU IMPIANA 3, KAWASAN PERINDUSTRIAN MENGLEMBU IMPIANA,",
        "31450 IPOH, PERAK",
    ],
    phoneNumber: "010-5605685",
    email: "inhome.ipoh@gmail.com",
};

const DEFAULT_PRINCIPLE_BUSINESS_DETAILS = {
    businessRegNo: "202101013675(1413974-K)",
    tin: "C 26675323100",
    salesTaxNo: "A10-2203-22000001",
    bankAccount: "PBB 381 879 9319",
};

const PRINCIPLE_BUSINESS_DETAIL_FIELDS = Object.keys(
    DEFAULT_PRINCIPLE_BUSINESS_DETAILS
);

const resolvePrincipleBusinessDetails = async (order) => {
    let principle = order?.principle;
    const principleId =
        principle && typeof principle === "object"
            ? principle._id ?? principle.id
            : principle || IPOH_ACCOUNT_ID;

    const hasDetails =
        principle &&
        typeof principle === "object" &&
        PRINCIPLE_BUSINESS_DETAIL_FIELDS.some((field) => principle[field]);

    if (!hasDetails && principleId) {
        try {
            const res = await publicRequest.get(`/users/find/${principleId}`);
            principle = res.data;
        } catch (err) {
            console.error("Failed to load principle account for invoice:", err);
        }
    }

    return PRINCIPLE_BUSINESS_DETAIL_FIELDS.reduce((details, field) => {
        const value =
            principle && typeof principle === "object"
                ? String(principle[field] || "").trim()
                : "";
        details[field] = value || DEFAULT_PRINCIPLE_BUSINESS_DETAILS[field];
        return details;
    }, {});
};

// Orders only store the add-on item code; the name lives on the "Add On" category product
const resolveAddOnNamesByCode = async () => {
    try {
        const res = await publicRequest.get("/products/");
        return (res.data || []).reduce((names, product) => {
            const categoryTitle = String(product?.category?.title || "")
                .trim()
                .toLowerCase();
            const itemCode = String(product?.itemCode || "").trim();
            const title = String(product?.title || "").trim();
            if (categoryTitle === "add on" && itemCode && title && !names[itemCode]) {
                names[itemCode] = title;
            }
            return names;
        }, {});
    } catch (err) {
        console.error("Failed to load add on products for invoice:", err);
        return {};
    }
};

const CONTACT_ICON_VIEWBOX = 24;
const CONTACT_ICON_PATHS = {
    phone:
        "M 3 5 A 2 2 0 0 1 5 3 H 8.28 A 1 1 0 0 1 9.23 3.684 L 10.73 8.177 A 1 1 0 0 1 10.23 9.387 L 7.97 10.517 A 11.042 11.042 0 0 0 13.49 16.033 L 14.62 13.776 A 1 1 0 0 1 15.83 13.276 L 20.32 14.774 A 1 1 0 0 1 21.27 15.723 V 19 A 2 2 0 0 1 19.27 21 H 18.27 C 10.28 21 3 13.72 3 5.72 V 5 Z",
    email:
        "M 3 8 L 10.89 13.26 A 2 2 0 0 0 13.11 13.26 L 21 8 M 5 19 H 19 A 2 2 0 0 0 21 17 V 7 A 2 2 0 0 0 19 5 H 5 A 2 2 0 0 0 3 7 V 17 A 2 2 0 0 0 5 19 Z",
    website:
        "M 12 3 A 9 9 0 0 1 12 21 A 9 9 0 0 1 12 3 M 21 12 A 9 9 0 0 0 3 12 M 21 12 H 3 M 12 3 C 13.66 3 15 7.03 15 12 C 15 16.97 13.66 21 12 21 M 12 3 C 10.34 3 9 7.03 9 12 C 9 16.97 10.34 21 12 21",
};

const drawContactIcon = (page, iconType, x, y, size) => {
    const path = CONTACT_ICON_PATHS[iconType];
    if (!path) return;

    const scale = size / CONTACT_ICON_VIEWBOX;
    page.drawSvgPath(path, {
        x,
        y: y - size * 0.85,
        scale,
        borderColor: rgb(0, 0, 0),
        borderWidth: 1.35,
    });
};

const drawContactInfoBlock = ({
    page,
    fontSize,
    valueFont,
    rows,
    yPositions,
    sanitizeText,
    rightEdge = 560,
    labelGap = 4,
    iconSize = 10,
    valueTextYOffset = 17,
}) => {
    const sanitizedRows = rows.map((row) => ({
        iconType: row.iconType,
        value: sanitizeText(row.value),
    }));

    const labelColumnWidth = iconSize + 1;
    const maxValueWidth = Math.max(
        ...sanitizedRows.map((row) =>
            valueFont.widthOfTextAtSize(row.value, fontSize)
        )
    );

    const blockWidth = labelColumnWidth + labelGap + maxValueWidth;
    const labelX = rightEdge - blockWidth;
    const valueX = labelX + labelColumnWidth + labelGap;

    sanitizedRows.forEach((row, index) => {
        const y = yPositions[index];
        drawContactIcon(page, row.iconType, labelX, y, iconSize);
        page.drawText(row.value, {
            x: valueX,
            y: y - valueTextYOffset,
            size: fontSize,
            font: valueFont,
            color: rgb(0, 0, 0),
        });
    });
};

const DELIVERY_ORDER_ACK_RESERVE = 112;

const drawDeliveryOrderAcknowledgement = ({
    page,
    standardFontSize,
    timesRomanFont,
}) => {
    const leftX = 30;
    const leftW = 220;
    const rightX = 330;
    const rightW = 230;
    const detailSize = 8;
    const details = [
        "Name / Company Name",
        "NRIC / Company Registration No",
        "Date",
    ];
    const dateY = 32;
    const detailGap = 14.5;
    const captionY = dateY + details.length * detailGap + 4;
    const signLineY = captionY + 13;
    const centerInColumn = (text, colX, colW, textY) => {
        const width = timesRomanFont.widthOfTextAtSize(text, standardFontSize);
        page.drawText(text, {
            x: colX + Math.max((colW - width) / 2, 0),
            y: textY,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });
    };

    page.drawLine({
        start: { x: leftX, y: signLineY },
        end: { x: leftX + leftW, y: signLineY },
        thickness: 0.7,
        color: rgb(0, 0, 0),
    });
    page.drawLine({
        start: { x: rightX, y: signLineY },
        end: { x: rightX + rightW, y: signLineY },
        thickness: 0.7,
        color: rgb(0, 0, 0),
    });

    centerInColumn("Prepared By", leftX, leftW, captionY);
    centerInColumn("Received By", rightX, rightW, captionY);

    details.forEach((label, index) => {
        const textY = dateY + (details.length - 1 - index) * detailGap;
        page.drawText(label, {
            x: rightX,
            y: textY,
            size: detailSize,
            font: timesRomanFont,
            color: rgb(0.25, 0.25, 0.25),
        });
        const labelWidth = timesRomanFont.widthOfTextAtSize(label, detailSize);
        page.drawLine({
            start: { x: rightX + labelWidth + 6, y: textY - 1 },
            end: { x: rightX + rightW, y: textY - 1 },
            thickness: 0.45,
            color: rgb(0.35, 0.35, 0.35),
        });
    });
};

const createCustomerInvoice = async (original, options = {}) => {
    const { mergeLabourIntoLastItem = false, isDeliveryOrder = false } = options;
    try {
        const NumberFormatter = new Intl.NumberFormat("en-GB", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });

        // Helper function to sanitize text for WinAnsi encoding
        const sanitizeText = (text) => {
            if (!text) return "";
            // Normalize typographic quotes/primes (e.g. feet/inches from mobile keyboards) to ASCII
            return String(text)
                .replace(/[\u2018\u2019\u2032\u02BC\u00B4\uFF07]/g, "'")
                .replace(/[\u201C\u201D\u2033\u2036]/g, '"')
                .replace(/[^\x20-\x7E\xA0-\xFF]/g, "")
                .replace(/\(\s*\)/g, "")
                .replace(/[ \t]{2,}/g, " ")
                .trim();
        };

        const capitalizeWords = (text) => {
            if (!text) return "";
            return String(text)
                .split(/\s+/)
                .filter(Boolean)
                .map(
                    (word) =>
                        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
                )
                .join(" ");
        };

        // Standard font size for all text
        const standardFontSize = 9;
        
        // Mapping for awning item fields to human-readable labels
        const awningFieldLabels = {
            remoteControl: "Remote Control",
            rainingSensor: "Raining Sensor",
            birdVoiceSensor: "Bird Voice Sensor",
            ledLight: "LED Light",
            metalDeckModel: "Model",
            glassColor: "Glass Color",
            fiberglassColor: "Fiber Glass Color",
            aluminiumFlutedPanelColor: "Aluminium Fluted Panel Color",
            aluminiumFlutedPanelFoamColor: "Aluminium Fluted Panel with Foam Color",
            polycarbonateColor: "Polycarbonate Color",
            metaldeckColor: "Metal Deck Color",
            puMetalColor: "PU Metal Color",
            puFoamColor: "PU Foam Color",
            puMetalInfo: "PU Metal Info",
            size: "Size",
            area: "Area",
            frame: "Frame",
            steelPillar: "Steel Pillar",
            steelPillarQuantity: "Steel Pillar Qty",
            ceilingMaterial: "Ceiling Material",
            ceilingColor: "Ceiling Color",
            acpColor: "ACP Color",
            replacementPanel: "Replacement Panel",
            sideHoarding: "Side Hoarding",
            poleSupport: "Pole Support",
            poleSupportQuantity: "Pole Support Qty",
            aluminiumPillar: "Aluminium Pillar",
            aluminiumPillarQuantity: "Aluminium Pillar Qty",
            stainlessSteelPillar: "Stainless Steel Pillar",
            stainlessPillarQuantity: "Stainless Steel Pillar Qty",
            iBeamPillar: "I-Beam Pillar",
            ibeamPillarQuantity: "I-Beam Pillar Qty",
            mainSupport: "Main Support",
            mainSupportQuantity: "Main Support Qty",
            iBeamMainSupport: "I-Beam Main Support",
            ibeamMainSupportQuantity: "I-Beam Main Support Qty",
            ibeamMainSupportSize: "I-Beam Main Support Size (ft)",
            flashingCoverSize: "Flashing Cover Size",
            aluminiumLouversDegree: "Aluminium Louvers Degree",
            aluminiumLouversSize: "Aluminium Louvers Size",
            aluminiumLouversColor: "Aluminium Louvers Color",
            gutter: "Gutter",
            gutterSize: "Gutter Size",
            giCover: "G.I Cover",
            giCoverSize: "G.I Cover Size",
            uPerlinCover: "U-Purlin Cover",
            uPerlinCoverSize: "U-Purlin Cover Size",
            aluminiumCover: "Aluminium Cover",
            aluminiumCoverSize: "Aluminium Cover Size",
            aluminiumSingleTLightProfile: "Aluminium Single T-Light Profile",
            aluminiumSingleTLightSize: "Aluminium Single T-Light Size",
            aluminiumDoubleTLightProfile: "Aluminium Double T-Light Profile",
            aluminiumDoubleTLightSize: "Aluminium Double T-Light Size",
            dismantleRoofSize: "Dismantle Existing Roof Size",
            crane: "Crane",
            craneDays: "Crane Days",
            skylift: "Skylift",
            skyliftDays: "Skylift Days",
            glassColor10_38: "Laminated Safety Glass 10.38mm Color",
            glassColor12_38: "Laminated Safety Glass 12.38mm Color",
            glassColor10_76: "Laminated Safety IR Glass 10.76mm Color",
            glassColor12_76: "Laminated Safety IR Glass 12.76mm Color",
            lamiTempered11_52: "Laminated Tempered 11.52mm",
            lamiTempered11_52Quantity: "Laminated Tempered 11.52mm QTY",
            lamiTempered13_52: "Laminated Tempered 13.52mm",
            lamiTempered13_52Quantity: "Laminated Tempered 13.52mm QTY",
            inbondColor: "In-Bond Color",
            pmsColor: "PMS Color",
            color3mm: "3mm Color",
            frameColor: "Frame Color",
            ceilingcapsize: "Ceiling Capping Size 2” ~ 8”",
            ceilingdropsize: "Ceiling Up",
            rorobinSize: "Roro Bin Size",
            dismantleCeilingSize: "Dismantle Existing Plaster Ceiling Size",
            gDmlColor: "DML Color (G)",
            sDmlColor: "DML Color (S)",
            gSpaceColor: "Space Color (G)",
            sSpaceColor: "Space Color (S)",
        };

        const awningTypeLabels = {
            glass: "Glass Roof",
            aluminiumComposite: "Aluminium Composite Roof",
            aluminiumCBond: "Aluminium C-Bond",
            polyCarbonate: "Polycarbonate Roof",
            metalDeck: "Metal Deck Roof",
            aluminiumStrip: "Aluminium Strip Ceiling",
            retractableSystem: "Retractable System",
            others: "Others",
        };

        const getAwningFieldLabel = (_item, field) => {
            if (field === "size") return "Size";
            return awningFieldLabels[field] || field;
        };

        /** Main invoice line title for awning catalog products (from items[0].type). */
        const awningMainTitleByItemType = {
            aluminiumComposite: "Aluminium Composite Panel C/W Metal Frame",
            aluminiumCBond: "Aluminium C-Bond",
            glass: "Safety Laminated Glass C/W Metal Frame",
            polyCarbonate: "Polycarbonate Sheet C/W Metal Frame",
            metalDeck: "Metal Deck PU Foam C/W Metal Frame",
            metalDeck2: "Metal Deck PU Foam C/W Metal Frame",
            retractableSystem: "Retracable System",
            retracableSystem: "Retracable System",
            aluminiumStrip: "Aluminium Strip Ceiling",
        };

        const getFirstProductItemType = (productRow) => {
            if (Array.isArray(productRow.items) && productRow.items.length > 0) {
                return productRow.items[0].type;
            }
            if (
                Array.isArray(productRow.productItems) &&
                productRow.productItems.length > 0
            ) {
                return productRow.productItems[0].type;
            }
            return productRow.type;
        };

        const getAwningMainTitle = (productRow) => {
            const itemType = getFirstProductItemType(productRow);
            if (itemType && awningMainTitleByItemType[itemType]) {
                return awningMainTitleByItemType[itemType];
            }
            return null;
        };

        /** Full Awning POS catalog product; other productIds use order `code` on invoice instead of generic "Awning" */
        const MAIN_AWNING_CATALOG_PRODUCT_ID =
            "69832bf1412f46e3c0c8dec7";

        const getOrderProductIdString = (pr) => {
            const pid = pr?.productId;
            if (pid == null) return "";
            if (typeof pid === "object") {
                const id = pid._id ?? pid.id;
                return id != null ? String(id) : "";
            }
            return String(pid);
        };

        const isMainAwningCatalogProduct = (pr) =>
            getOrderProductIdString(pr) === MAIN_AWNING_CATALOG_PRODUCT_ID;

        // Group rows by each product entry:
        // all items inside one product share the same invoice number/description.
        const buildProductRows = () =>
            original.products.map((product) => {
                const productItems =
                    Array.isArray(product.items) && product.items.length > 0
                        ? product.items.map((item) => ({
                              ...item,
                              isNonAwning: false,
                          }))
                        : [
                              {
                                  type: product.type,
                                  typeDescription: product.typeDescription,
                                  itemCode: product.itemCode,
                                  frameColor: product.frameColor,
                                  size: product.size,
                                  model: product.model,
                                  isNonAwning: true,
                              },
                          ];

                return {
                    ...product,
                    productItems,
                };
            });

        const getRowAreaKey = (row) => {
            if (row.productItems.some((item) => item.isNonAwning)) return "";
            const areas = row.productItems.map((item) =>
                String(item.area || "").trim().toLowerCase()
            );
            if (!areas[0] || areas.some((area) => area !== areas[0])) return "";
            return areas[0];
        };

        // Rows sharing the same area are listed under one invoice number
        const mergeProductRowsByArea = (rows) => {
            const merged = [];
            const rowsByArea = new Map();
            rows.forEach((row) => {
                const areaKey = getRowAreaKey(row);
                const taggedItems = row.productItems.map((item) => ({
                    ...item,
                    sourceRow: row,
                }));
                if (areaKey && rowsByArea.has(areaKey)) {
                    const target = rowsByArea.get(areaKey);
                    target.sourceRows.push(row);
                    target.productItems.push(...taggedItems);
                    return;
                }
                const entry = {
                    ...row,
                    productItems: taggedItems,
                    sourceRows: [row],
                    mergedArea: areaKey ? row.productItems[0].area : "",
                };
                if (areaKey) rowsByArea.set(areaKey, entry);
                merged.push(entry);
            });
            return merged;
        };

        const productRows = mergeProductRowsByArea(buildProductRows());

        const needsAddOnNames = (original.products || []).some((product) => {
            const code = String(product?.code ?? "").trim();
            return (
                code !== "" &&
                code.toLowerCase() !== "undefined" &&
                !isMainAwningCatalogProduct(product)
            );
        });
        const addOnNamesByCode = needsAddOnNames
            ? await resolveAddOnNamesByCode()
            : {};
        const formatProductCodeText = (code) => {
            const codeStr = String(code ?? "").trim();
            const name = addOnNamesByCode[codeStr];
            if (!codeStr || !name || name.toLowerCase() === codeStr.toLowerCase()) {
                return codeStr;
            }
            return `${codeStr} - ${name}`;
        };

        const COMBINED_ITEMS_PRINCIPLE_ID = "686fc107e6d68e77cd2512f7";
        const getOrderPrincipleId = (order) => {
            const principle = order?.principle;
            if (principle == null) return "";
            if (typeof principle === "object") {
                const id = principle._id ?? principle.id;
                return id != null ? String(id) : "";
            }
            return String(principle);
        };
        const useCombinedProductItemsLayout =
            getOrderPrincipleId(original) === COMBINED_ITEMS_PRINCIPLE_ID;

        // Order line is "Others" (POS / manual): show product.code on invoice, not generic "Awning"
        const isOthersOrderLine = (pr) => {
            const ic = String(pr?.itemCode || "").toUpperCase();
            if (ic === "OTHERS") return true;
            if (String(pr?.type || "").toLowerCase() === "others") return true;
            if (String(pr?.typeDescription || "").toLowerCase() === "others")
                return true;
            const pid = pr?.productId;
            if (pid && typeof pid === "object") {
                if (String(pid.title || "").toLowerCase() === "others")
                    return true;
            }
            if (Array.isArray(pr?.items)) {
                return pr.items.some((it) => {
                    const typ = String(it?.type || "").toLowerCase();
                    const td = String(it?.typeDescription || "").toLowerCase();
                    return typ === "others" || td === "others";
                });
            }
            return false;
        };

        /** Prefer order `code` for invoice when it is not the fixed OTHERS sentinel */
        const othersInvoicePrimaryText = (pr) => {
            const raw = pr?.code;
            if (raw === undefined || raw === null) return "";
            const s = String(raw).trim();
            if (!s || s.toUpperCase() === "OTHERS") return "";
            return s;
        };

        const hasMeaningfulAddressPart = (v) => {
            if (v === undefined || v === null) return false;
            const s = String(v).trim();
            return s !== "" && s !== "-";
        };
        const pickInvoiceAddress = (billing, service) =>
            hasMeaningfulAddressPart(billing) ? billing : service ?? "";
        const invoiceAddrLine1 = pickInvoiceAddress(
            original.billingLineOne,
            original.lineOne
        );
        const invoiceAddrLine2 = pickInvoiceAddress(
            original.billingLineTwo,
            original.lineTwo
        );
        const invoiceZip = pickInvoiceAddress(original.billingZip, original.zip);
        const invoiceCity = pickInvoiceAddress(
            original.billingCity,
            original.city
        );
        const invoiceCountry = pickInvoiceAddress(
            original.billingCountry,
            original.country
        );
        const invoiceAddressFirstLine = (() => {
            const line1 = String(invoiceAddrLine1 || "").trim();
            const line2 = String(invoiceAddrLine2 || "").trim();
            if (!hasMeaningfulAddressPart(line1) && !hasMeaningfulAddressPart(line2)) {
                return "";
            }
            if (!hasMeaningfulAddressPart(line1)) return line2;
            if (!hasMeaningfulAddressPart(line2)) return `${line1},`;
            return `${line1}, ${line2}`;
        })();

        // Create a new PDFDocument
        const pdfDoc = await PDFDocument.create();

        // Embed the Times Roman font
        const timesRomanFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
        const timesRomanBoldFont = await pdfDoc.embedFont(
            StandardFonts.TimesRomanBold
        );

        // Use order amount (cart + expenses) as invoice total
        const totalAmount = resolveInvoiceTotalAmount(original);

        const fontSize = 20;
        const bottomMargin = 40; // Minimum space to leave at bottom before creating new page
        const tableLineStep = 0.5;
        const tableRowGap = 0.4;
        const subItemHeaderGap = tableLineStep + tableRowGap;
        const qtyColLeft = 508;
        const qtyColRight = 545;
        const getQtyX = (text) => {
            if (!isDeliveryOrder) return 320;
            const width = timesRomanFont.widthOfTextAtSize(
                String(text),
                standardFontSize
            );
            return qtyColLeft + (qtyColRight - qtyColLeft - width) / 2;
        };
        const colUnitPrice = 368;
        const colSst = 442;
        const colAmount = 498;
        const lineTitleFontSize = 10;
        const lineTitleDetailGap = 0.15;
        
        // Helper to get item details
        const getItemDetails = (
            item,
            { skipArea = false, fieldFilter = () => true } = {}
        ) => {
            const details = [];

            if (item.isNonAwning) {
                [
                    ["itemCode", "Item Code"],
                    ["type", "Type"],
                    ["frameColor", "Frame Colour"],
                    ["size", "Size"],
                    ["model", "Model"],
                ].forEach(([field, label]) => {
                    if (item[field] && fieldFilter(field)) {
                        details.push(`${label}: ${item[field]}`);
                    }
                });
            } else {
                const pushField = (field, label) => {
                    if (!fieldFilter(field)) return;
                    const value = item[field];
                    if (
                        value === undefined ||
                        value === null ||
                        value === "" ||
                        value === false
                    ) {
                        return;
                    }
                    const displayValue =
                        typeof value === "boolean"
                            ? "Yes"
                            : field === "area"
                              ? capitalizeWords(String(value))
                              : formatAwningSupplementaryValue(field, value);
                    details.push(`${label}: ${displayValue}`);
                };
                // Area directly under the main title line
                if (!skipArea) {
                    pushField("area", getAwningFieldLabel(item, "area"));
                }
                Object.entries(awningFieldLabels).forEach(([field]) => {
                    if (field === "area") return;
                    pushField(field, getAwningFieldLabel(item, field));
                });
            }

            return details;
        };

        const getItemTypeLabel = (item, productRow) => {
            if (item.isNonAwning) {
                const isOthersNonAwning =
                    String(item.type || "").toLowerCase() === "others" ||
                    String(item.typeDescription || "").toLowerCase() ===
                        "others" ||
                    String(item.itemCode || "").toUpperCase() === "OTHERS";
                if (isOthersNonAwning) {
                    return (
                        othersInvoicePrimaryText(productRow) ||
                        item.typeDescription ||
                        item.type ||
                        "Item"
                    );
                }
                if (!isMainAwningCatalogProduct(productRow)) {
                    const codeStr = formatProductCodeText(productRow.code);
                    return (
                        codeStr ||
                        item.typeDescription ||
                        item.type ||
                        "Item"
                    );
                }
                return item.typeDescription || item.type || "Item";
            }
            if (String(item.type || "").toLowerCase() === "others") {
                return (
                    othersInvoicePrimaryText(productRow) ||
                    item.typeDescription ||
                    productRow.code ||
                    awningTypeLabels.others
                );
            }
            return (
                awningTypeLabels[item.type] ||
                item.typeDescription ||
                item.type ||
                "Item"
            );
        };

        const getProductRowDetails = (
            productRow,
            { fieldFilter, includeCombinedTitle = true } = {}
        ) => {
            const details = [];
            const hasMultipleItems = productRow.productItems.length > 1;
            const isAreaMerged = (productRow.sourceRows?.length || 0) > 1;
            const isFiltered = typeof fieldFilter === "function";
            const itemDetailOptions = {
                skipArea: isAreaMerged,
                ...(isFiltered ? { fieldFilter } : {}),
            };

            if (isAreaMerged && (!isFiltered || fieldFilter("area"))) {
                details.push(
                    `${awningFieldLabels.area}: ${capitalizeWords(String(productRow.mergedArea))}`
                );
            }

            if (useCombinedProductItemsLayout && hasMultipleItems) {
                const combinedTitle = productRow.productItems
                    .map((item) => getItemTypeLabel(item, item.sourceRow || productRow))
                    .filter(Boolean)
                    .join(" + ");
                if (combinedTitle && includeCombinedTitle) {
                    details.push(combinedTitle);
                }
                productRow.productItems.forEach((item) => {
                    getItemDetails(item, itemDetailOptions).forEach((detail) => {
                        details.push(detail);
                    });
                });
                return details;
            }

            productRow.productItems.forEach((item) => {
                const subDetails = getItemDetails(item, itemDetailOptions);

                subDetails.forEach((detail) => {
                    details.push(detail);
                });
            });

            return details;
        };

        // Create first page with header
        let currentPage = pdfDoc.addPage();
        let pageHeight = currentPage.getSize().height;
        const defaultPageWidth = currentPage.getSize().width;
        const defaultPageHeight = pageHeight;
        let currentY = 2; // Start position (will be updated after header)
        let isFirstPage = true;
        let currentItemNumber = 0;
        let hasMultiplePages = false;

        // Function to draw table headers
        const drawTableHeaders = (page, yPosition) => {
            page.drawLine({
                start: { x: 30, y: pageHeight - (yPosition - 0.5) * fontSize },
                end: { x: 560, y: pageHeight - (yPosition - 0.5) * fontSize },
                color: rgb(0, 0, 0),
                opacity: 0.75,
            });

            page.drawText("No", {
                x: 40,
                y: pageHeight - yPosition * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("Description", {
                x: 80,
                y: pageHeight - yPosition * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            page.drawText("Qty", {
                x: getQtyX("Qty"),
                y: pageHeight - yPosition * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            if (!isDeliveryOrder) {
                page.drawText("U/ Price (RM)", {
                    x: colUnitPrice,
                    y: pageHeight - yPosition * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText("SST (5%)", {
                    x: colSst,
                    y: pageHeight - yPosition * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                page.drawText("Amount (RM)", {
                    x: colAmount,
                    y: pageHeight - yPosition * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            }

            page.drawLine({
                start: { x: 30, y: pageHeight - (yPosition + 0.4) * fontSize },
                end: { x: 560, y: pageHeight - (yPosition + 0.4) * fontSize },
                color: rgb(0, 0, 0),
                opacity: 0.75,
            });
        };

        // Function to create a new page
        const createNewPage = () => {
            currentPage = pdfDoc.addPage();
            pageHeight = currentPage.getSize().height;
            currentY = 2; // Start near top
            isFirstPage = false;
            hasMultiplePages = true;
            
            // Draw table headers on new page
            drawTableHeaders(currentPage, currentY);
            currentY = 3.5; // Position after headers
        };

        // Function to check if we need a new page
        const needsNewPage = (linesNeeded = 1) => {
            const remainingY = pageHeight - (currentY + linesNeeded) * fontSize;
            const minBottom = isDeliveryOrder
                ? DELIVERY_ORDER_ACK_RESERVE
                : bottomMargin;
            return remainingY < minBottom;
        };

        // Render header on first page
        const principleHeader = IPOH_INVOICE_HEADER;
        const principleBusinessDetails = await resolvePrincipleBusinessDetails(original);
        const principleDetailLines = [
            `Business Registration Number : ${principleBusinessDetails.businessRegNo}`,
            `Business TIN : ${principleBusinessDetails.tin}`,
            `Business Sales Tax Number : ${principleBusinessDetails.salesTaxNo}`,
            `Bank Account Number : ${principleBusinessDetails.bankAccount}`,
        ];
        const logoUrl =
            "https://ik.imagekit.io/dnddecpho/IHGroup/inhomelogo-new_80yctapLk.png";
        const logoBytes = await fetch(logoUrl).then((res) => res.arrayBuffer());
        const logoImage = await pdfDoc.embedPng(logoBytes);
        const logoArt = { left: 308, top: 92, right: 1142, bottom: 698 };
        const logoScale = 75 / (logoArt.right - logoArt.left);
        const qrUrl = "/duitnow-qr.png";
        const qrImageBytes = await fetch(qrUrl).then((res) => res.arrayBuffer());
        const qrImage = await pdfDoc.embedPng(qrImageBytes);
        currentPage.drawImage(logoImage, {
            x: 20 - logoArt.left * logoScale,
            y:
                pageHeight -
                4 * fontSize -
                (logoImage.height - logoArt.bottom) * logoScale,
            width: logoImage.width * logoScale,
            height: logoImage.height * logoScale,
        });

        currentPage.drawText(sanitizeText(principleHeader.name), {
            x: 130,
            y: pageHeight - 1.9 * fontSize,
            size: standardFontSize,
            font: timesRomanBoldFont,
            color: rgb(0, 0, 0),
        });

        [...principleHeader.addressLines, ...principleDetailLines].forEach(
            (line, index) => {
                currentPage.drawText(sanitizeText(line), {
                    x: 130,
                    y: pageHeight - (2.5 + index * 0.55) * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            }
        );

        drawContactInfoBlock({
            page: currentPage,
            fontSize: standardFontSize,
            valueFont: timesRomanFont,
            sanitizeText,
            rows: [
                { iconType: "phone", value: principleHeader.phoneNumber },
                { iconType: "email", value: principleHeader.email },
                { iconType: "website", value: "www.inhome.com.my" },
            ],
            yPositions: [
                pageHeight - 4.1 * fontSize,
                pageHeight - 4.8 * fontSize,
                pageHeight - 5.5 * fontSize,
            ],
        });

        const documentTitle = isDeliveryOrder
            ? "DELIVERY ORDER"
            : original.paymentStatus === "completed"
              ? "INVOICE"
              : "SALES ORDER";
        const documentTitleSize = 15;
        const documentTitleTracking = 3;
        const documentTitleColor = rgb(0.15, 0.15, 0.15);
        const documentTitleRuleColor = rgb(0.7, 0.7, 0.7);
        const documentTitleY = pageHeight - 7.45 * fontSize;
        const documentTitleChars = documentTitle.split("");
        const documentTitleWidth =
            documentTitleChars.reduce(
                (width, char) =>
                    width + timesRomanBoldFont.widthOfTextAtSize(char, documentTitleSize),
                0
            ) +
            documentTitleTracking * (documentTitleChars.length - 1);
        const documentTitleStartX = 295 - documentTitleWidth / 2;
        let documentTitleX = documentTitleStartX;
        documentTitleChars.forEach((char) => {
            currentPage.drawText(char, {
                x: documentTitleX,
                y: documentTitleY,
                size: documentTitleSize,
                font: timesRomanBoldFont,
                color: documentTitleColor,
            });
            documentTitleX +=
                timesRomanBoldFont.widthOfTextAtSize(char, documentTitleSize) +
                documentTitleTracking;
        });
        const documentTitleRuleY = documentTitleY + documentTitleSize * 0.33;
        [
            [30, documentTitleStartX - 14],
            [documentTitleStartX + documentTitleWidth + 14, 560],
        ].forEach(([startX, endX]) => {
            currentPage.drawLine({
                start: { x: startX, y: documentTitleRuleY },
                end: { x: endX, y: documentTitleRuleY },
                thickness: 0.75,
                color: documentTitleRuleColor,
            });
        });

        currentPage.drawText(`Invoice ID:`, {
            x: 25,
            y: pageHeight - 8.6 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(`${_.padStart(original.invoiceId, 4, "0")}`, {
            x: 140,
            y: pageHeight - 8.6 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(
            isDeliveryOrder ? `Delivery Order ID:` : `Sales Order ID:`,
            {
                x: 25,
                y: pageHeight - 9.3 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        currentPage.drawText(
            sanitizeText(
                isDeliveryOrder
                    ? original.deliveryOrderNo || "-"
                    : original.salesOrderId || "-"
            ),
            {
                x: 140,
                y: pageHeight - 9.3 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        currentPage.drawText(`CO Number:`, {
            x: 25,
            y: pageHeight - 10 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(sanitizeText(original.coNumber), {
            x: 140,
            y: pageHeight - 10 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(`Date of Issue:`, {
            x: 25,
            y: pageHeight - 10.7 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(
            `${moment(original.createdAt).format("DD MMM YYYY")}`,
            {
                x: 140,
                y: pageHeight - 10.7 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        currentPage.drawText(`Payment Terms:`, {
            x: 25,
            y: pageHeight - 11.4 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText("C.O.D.", {
            x: 140,
            y: pageHeight - 11.4 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(`Accounts Name:`, {
            x: 25,
            y: pageHeight - 12.1 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(
            sanitizeText(capitalizeWords(original?.userId?.name)),
            {
                x: 140,
                y: pageHeight - 12.1 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        currentPage.drawText(`Phone Number:`, {
            x: 25,
            y: pageHeight - 12.8 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(sanitizeText(original?.userId?.phoneNumber), {
            x: 140,
            y: pageHeight - 12.8 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(`Email:`, {
            x: 25,
            y: pageHeight - 13.5 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(sanitizeText(original?.userId?.email), {
            x: 140,
            y: pageHeight - 13.5 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(`Address:`, {
            x: 25,
            y: pageHeight - 14.2 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(
            sanitizeText(invoiceAddressFirstLine),
            {
                x: 140,
                y: pageHeight - 14.2 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        currentPage.drawText(
            sanitizeText(`${invoiceZip}, ${invoiceCity}, ${invoiceCountry}`),
            {
                x: 140,
                y: pageHeight - 14.9 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );
        
        currentPage.drawLine({
            start: {
                x: 30,
                y: pageHeight - 15.7 * fontSize,
            },
            end: {
                x: 560,
                y: pageHeight - 15.7 * fontSize,
            },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        currentPage.drawText("No", {
            x: 40,
            y: pageHeight - 16.3 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText("Description", {
            x: 80,
            y: pageHeight - 16.3 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText("Qty", {
            x: getQtyX("Qty"),
            y: pageHeight - 16.3 * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        if (!isDeliveryOrder) {
            currentPage.drawText("U/ Price (RM)", {
                x: colUnitPrice,
                y: pageHeight - 16.3 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            currentPage.drawText("SST (5%)", {
                x: colSst,
                y: pageHeight - 16.3 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            currentPage.drawText("Amount (RM)", {
                x: colAmount,
                y: pageHeight - 16.3 * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
        }

        currentPage.drawLine({
            start: {
                x: 30,
                y: pageHeight - 16.7 * fontSize,
            },
            end: {
                x: 560,
                y: pageHeight - 16.7 * fontSize,
            },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        // Set starting Y position after header
        currentY = 18.2;

        const sstIncluded = original.sstIncluded === true;
        const roundMoney = (value) =>
            Math.round((value + Number.EPSILON) * 100) / 100;

        const {
            productUnitPrice,
            productSst,
            productAmountIncl,
            labourCharge,
        } = getInvoiceSalesBreakdown(original, totalAmount);

        // Main item description — Others lines use order `code` (product.code), not "Awning"
        const getRowMainDescription = (row) => {
            const customTitle = String(row.title ?? "").trim();
            if (customTitle) return capitalizeWords(customTitle);
            if (isOthersOrderLine(row)) {
                return (
                    othersInvoicePrimaryText(row) ||
                    row.typeDescription ||
                    row.type ||
                    awningTypeLabels.others
                );
            }
            if (isMainAwningCatalogProduct(row)) {
                return (
                    getAwningMainTitle(row) ||
                    row.typeDescription ||
                    awningTypeLabels[row.type] ||
                    row.type ||
                    "Awning"
                );
            }
            const codeStr = formatProductCodeText(row.code);
            return (
                codeStr ||
                row.typeDescription ||
                awningTypeLabels[row.type] ||
                row.type ||
                "Item"
            );
        };

        const getRowTitle = (row) =>
            [
                ...new Set((row.sourceRows || [row]).map(getRowMainDescription)),
            ].join(" + ");

        const splitNameAddOns = (title) => {
            const parts = String(title ?? "")
                .split(/\s+\+\s+/)
                .map((part) => sanitizeText(part))
                .filter(Boolean);
            if (parts.length < 2) {
                return { title: parts[0] || sanitizeText(title), addOns: [] };
            }
            return {
                title: parts[0],
                addOns: [`AddOn: ${parts.slice(1).join(" + ")}`],
            };
        };

        const getRowQuantity = (row) =>
            (row.sourceRows?.length || 0) > 1
                ? 1
                : row.quantity || row.amount || 1;

        const SPLIT_PRODUCT_FIELDS = new Set([
            "area",
            "size",
            "frame",
            "frameColor",
            "steelPillar",
            "steelPillarQuantity",
            "aluminiumPillar",
            "aluminiumPillarQuantity",
            "stainlessSteelPillar",
            "stainlessPillarQuantity",
            "iBeamPillar",
            "ibeamPillarQuantity",
            "poleSupport",
            "poleSupportQuantity",
            "mainSupport",
            "mainSupportQuantity",
            "iBeamMainSupport",
            "ibeamMainSupportQuantity",
            "ibeamMainSupportSize",
        ]);
        const isSplitProductField = (field) => SPLIT_PRODUCT_FIELDS.has(field);
        const isSplitLabourField = (field) =>
            field === "area" || !SPLIT_PRODUCT_FIELDS.has(field);

        // Awning products are saved with code "undefined"; add-on / others products carry a real code
        const hasProductCode = (row) => {
            const code = String(row?.code ?? "").trim();
            return code !== "" && code.toLowerCase() !== "undefined";
        };
        const isLabourAreaProductRow = (row) =>
            (row.sourceRows || [row]).every(
                (sourceRow) =>
                    hasProductCode(sourceRow) &&
                    !isMainAwningCatalogProduct(sourceRow)
            );

        const getLabourAreaRowDetails = (row) => {
            const quantity = getRowQuantity(row);
            const split = splitNameAddOns(getRowTitle(row));
            const title = `- ${split.title}${quantity > 1 ? ` x ${quantity}` : ""}`;
            return [
                title,
                ...split.addOns.map((detail) => `  ${detail}`),
                ...getProductRowDetails(row).map((detail) => `  ${detail}`),
            ];
        };

        // Second-last line carries the product (SST) charge, last line the labour charge;
        // a single product is split so both charges get their own line.
        // Coded products (add-ons / others) are listed under the labour line when awning products exist.
        const buildInvoiceLines = () => {
            const labourAreaRows = mergeLabourIntoLastItem
                ? []
                : productRows.filter(isLabourAreaProductRow);
            const movesToLabourArea =
                labourAreaRows.length > 0 &&
                labourAreaRows.length < productRows.length;
            const mainRows = movesToLabourArea
                ? productRows.filter((row) => !isLabourAreaProductRow(row))
                : productRows;
            const labourAreaDetails = movesToLabourArea
                ? labourAreaRows.flatMap(getLabourAreaRowDetails)
                : [];

            const lines = mainRows.map((row) => {
                const split = splitNameAddOns(getRowTitle(row));
                return {
                    title: split.title,
                    addOns: split.addOns,
                    quantity: getRowQuantity(row),
                    details: getProductRowDetails(row),
                    pricing: null,
                };
            });
            const withAddOns = (line) => ({
                ...line,
                details: [...(line.addOns || []), ...line.details],
            });

            if (mergeLabourIntoLastItem) {
                if (lines.length > 0) lines[lines.length - 1].pricing = "merged";
                return lines.map(withAddOns);
            }

            if (movesToLabourArea && lines.length > 1) {
                lines[lines.length - 1].pricing = "product";
                return [
                    ...lines.map(withAddOns),
                    {
                        title: "Materials + Labour Installation Charge",
                        quantity: 1,
                        details: labourAreaDetails,
                        pricing: "labour",
                    },
                ];
            }

            if (lines.length === 0) {
                return [
                    {
                        title: "Labour / Installation Charge",
                        quantity: 1,
                        details: [],
                        pricing: "labour",
                    },
                ];
            }

            if (lines.length === 1) {
                const [row] = mainRows;
                return [
                    {
                        title: lines[0].title,
                        quantity: lines[0].quantity,
                        details: [
                            ...(lines[0].addOns || []),
                            ...getProductRowDetails(row, {
                                fieldFilter: isSplitProductField,
                            }),
                        ],
                        pricing: "product",
                    },
                    {
                        title: "Materials + Labour Installation Charge",
                        quantity: 1,
                        details: [
                            ...getProductRowDetails(row, {
                                fieldFilter: isSplitLabourField,
                                includeCombinedTitle: false,
                            }),
                            ...labourAreaDetails,
                        ],
                        pricing: "labour",
                    },
                ];
            }

            lines[lines.length - 2].pricing = "product";
            lines[lines.length - 1].pricing = "labour";
            return lines.map(withAddOns);
        };

        const getLinePricing = (pricing) => {
            if (pricing === "product") {
                return sstIncluded
                    ? {
                          unitPrice: productUnitPrice,
                          sst: productSst,
                          amount: productAmountIncl,
                      }
                    : null;
            }
            if (pricing === "labour") {
                return { unitPrice: labourCharge, amount: labourCharge };
            }
            if (pricing === "merged") {
                return sstIncluded
                    ? {
                          unitPrice: roundMoney(productUnitPrice + labourCharge),
                          sst: productSst,
                          amount: roundMoney(productAmountIncl + labourCharge),
                      }
                    : { unitPrice: labourCharge, amount: labourCharge };
            }
            return null;
        };

        const invoiceLines = buildInvoiceLines();

        for (let lineIndex = 0; lineIndex < invoiceLines.length; lineIndex++) {
            const invoiceLine = invoiceLines[lineIndex];
            const rowDetails = invoiceLine.details;

            currentItemNumber = lineIndex + 1;

            // For items after the first, try to keep item header together with at least some details
            // For the first item, always start on page 1 (don't jump to new page immediately)
            if (lineIndex > 0) {
                // Keep at least the header + 3 detail lines together (or all if fewer)
                const minLinesToKeepTogether = Math.min(5, 2 + rowDetails.length);
                if (needsNewPage(minLinesToKeepTogether)) {
                    createNewPage();
                }
            }
            
            // Check if we need new page just for the header line
            if (needsNewPage(1)) {
                createNewPage();
            }
            
            // Draw item number
            currentPage.drawText(`${currentItemNumber}`, {
                x: 40,
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
                weight: 600,
            });

            currentPage.drawText(sanitizeText(invoiceLine.title), {
                x: 80,
                y: pageHeight - currentY * fontSize,
                size: lineTitleFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            // Quantity on the same line as main description
            currentPage.drawText(`${invoiceLine.quantity}`, {
                x: getQtyX(`${invoiceLine.quantity}`),
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            const linePricing = getLinePricing(invoiceLine.pricing);
            if (!isDeliveryOrder && linePricing) {
                currentPage.drawText(
                    `${NumberFormatter.format(linePricing.unitPrice)}`,
                    {
                        x: colUnitPrice,
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );

                if (linePricing.sst !== undefined) {
                    currentPage.drawText(
                        `${NumberFormatter.format(linePricing.sst)}`,
                        {
                            x: colSst,
                            y: pageHeight - currentY * fontSize,
                            size: standardFontSize,
                            font: timesRomanFont,
                            color: rgb(0, 0, 0),
                        }
                    );
                }

                currentPage.drawText(
                    `${NumberFormatter.format(linePricing.amount)}`,
                    {
                        x: colAmount,
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );
            }

            currentY += tableLineStep; // Move to next line for details
            if (rowDetails.length > 0) {
                currentY += lineTitleDetailGap;
            }

            // Render grouped row details, creating new pages as needed
            for (let d = 0; d < rowDetails.length; d++) {
                const detail = rowDetails[d];
                const addSubItemGap = d > 0 && detail.startsWith("- ");
                const linesNeeded = addSubItemGap
                    ? 1 + subItemHeaderGap / tableLineStep
                    : 1;

                // Check if we need a new page
                if (needsNewPage(linesNeeded)) {
                    createNewPage();
                }

                if (addSubItemGap) {
                    currentY += subItemHeaderGap;
                }

                currentPage.drawText(sanitizeText(detail), {
                    x: 80,
                    y: pageHeight - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                currentY += tableLineStep;
            }

            currentY += tableLineStep; // Blank line after item details
            currentY += tableRowGap; // Spacing between grouped product rows
        }

        const includedExpenseLines = [
            ["threedDrawing", "3D Drawing"],
            ["crane", "Crane"],
        ]
            .filter(([key]) => (parseFloat(original.expenses?.[key]) || 0) > 0)
            .map(([, label]) => label);

        const footerLinesNeeded = 8 + includedExpenseLines.length + 6;
        if (needsNewPage(footerLinesNeeded)) {
            createNewPage();
        }

        // Add warranty information after the invoice lines
        currentY += 0.6; // Spacing before warranty section

        if (includedExpenseLines.length > 0) {
            includedExpenseLines.forEach((label) => {
                currentPage.drawText(label, {
                    x: 80,
                    y: pageHeight - currentY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                currentY += 0.5;
            });
            currentY += 0.3;
        }

        const servicesIncludedLines = String(original.extraWarranty || "")
            .replace(/\t/g, "    ")
            .split(/\r\n|\r|\n/);
        while (servicesIncludedLines.length && !servicesIncludedLines[0].trim()) {
            servicesIncludedLines.shift();
        }
        while (
            servicesIncludedLines.length &&
            !servicesIncludedLines[servicesIncludedLines.length - 1].trim()
        ) {
            servicesIncludedLines.pop();
        }

        if (servicesIncludedLines.length > 0) {
            if (needsNewPage(2)) {
                createNewPage();
            }
            currentPage.drawText("Price Includes:", {
                x: 80,
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });
            currentY += 0.6;

            servicesIncludedLines.forEach((line) => {
                if (needsNewPage(1)) {
                    createNewPage();
                }
                if (line.trim()) {
                    currentPage.drawText(sanitizeText(line), {
                        x: 80,
                        y: pageHeight - currentY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    });
                }
                currentY += 0.5;
            });
            currentY += 0.3;
        }

        // Warranty section header
        if (!isDeliveryOrder) {
        currentPage.drawText("Warranty Information:", {
            x: 80,
            y: pageHeight - currentY * fontSize,
            size: standardFontSize,
            font: timesRomanBoldFont,
            color: rgb(0, 0, 0),
        });
        
        currentY += 0.6;
        
        const warrantyItems = buildOrderWarrantyTexts(original);
        
        warrantyItems.forEach((warranty) => {
            currentPage.drawText(warranty, {
                x: 80,
                y: pageHeight - currentY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });
            currentY += 0.5;
        });
        
        currentY += 0.6; // Spacing after warranty section
        }

        if (isDeliveryOrder) {
            if (needsNewPage(0)) {
                createNewPage();
            }
            drawDeliveryOrderAcknowledgement({
                page: currentPage,
                standardFontSize,
                timesRomanFont,
            });
        } else {

        // Calculate dynamic Y position based on actual content height
        const contentHeight = currentY + 2; // Add some padding

        currentPage.drawLine({
            start: {
                x: 30,
                y: pageHeight - contentHeight * fontSize,
            },
            end: {
                x: 560,
                y: pageHeight - contentHeight * fontSize,
            },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        currentPage.drawText(`SUB TOTAL:`, {
            x: 370,
            y: pageHeight - (contentHeight + 1) * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(
            `${
                process.env.REACT_APP_COUNTRY === "SINGAPORE"
                    ? "$"
                    : "RM"
            } ${NumberFormatter.format(original.paymentStatus === "installment" ? (original.deposit || 0) : totalAmount)}`,
            {
                x: 480,
                y: pageHeight - (contentHeight + 1) * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        let totalY = contentHeight + 1;
        if (original.discount !== 0) {
            totalY += 0.7;
            currentPage.drawText(`DISCOUNT:`, {
                x: 370,
                y: pageHeight - totalY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            currentPage.drawText(
                `${
                    process.env.REACT_APP_COUNTRY === "SINGAPORE"
                        ? "$"
                        : "RM"
                } ${NumberFormatter.format(original.discount)}`,
                {
                    x: 480,
                    y: pageHeight - totalY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                }
            );
        }

        if (original.credit) {
            totalY += 0.7;
            currentPage.drawText(`TRADE IN:`, {
                x: 370,
                y: pageHeight - totalY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            });

            currentPage.drawText(
                `${
                    process.env.REACT_APP_COUNTRY === "SINGAPORE"
                        ? "$"
                        : "RM"
                } ${NumberFormatter.format(original.credit.credit)}`,
                {
                    x: 480,
                    y: pageHeight - totalY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                }
            );
        }

        totalY += 0.7;

        // TOTAL
        totalY += 0.7;
        currentPage.drawText(`TOTAL:`, {
            x: 370,
            y: pageHeight - totalY * fontSize,
            size: standardFontSize,
            font: timesRomanFont,
            color: rgb(0, 0, 0),
        });

        currentPage.drawText(
            `${
                process.env.REACT_APP_COUNTRY === "SINGAPORE"
                    ? "$"
                    : "RM"
            } ${NumberFormatter.format(original.paymentStatus === "installment" ? (original.deposit || 0) : (totalAmount - original.discount - (original.credit ? original.credit.credit : 0)))}`,
            {
                x: 480,
                y: pageHeight - totalY * fontSize,
                size: standardFontSize,
                font: timesRomanFont,
                color: rgb(0, 0, 0),
            }
        );

        // Only show payment details if paymentStatus is not "installment" and not (draft with deposit 0)
        if (original.paymentStatus !== "installment" && !(original.status === "draft" && original.deposit === 0)) {
            const isMeaningfulDeposit = (v) => {
                if (v === undefined || v === null) return false;
                if (v === "" || v === "-") return false;
                if (typeof v === "string" && String(v).trim() === "-") return false;
                const n = Number(v);
                return Number.isFinite(n);
            };
            const currencyPrefix =
                process.env.REACT_APP_COUNTRY === "SINGAPORE" ? "$" : "RM";
            const isCompleted = original.paymentStatus === "completed";
            const laterPaymentAmount = (slot) => {
                if (isMeaningfulDeposit(original[paymentAmountField(slot)])) {
                    return Number(original[paymentAmountField(slot)]);
                }
                if (slot.depositField && isMeaningfulDeposit(original[slot.depositField])) {
                    return Number(original[slot.depositField]);
                }
                return null;
            };

            let totalPayments = Number(original.firstPayment || original.deposit || 0);
            PAYMENT_SLOTS.forEach((slot, index) => {
                const amount = index === 0
                    ? (original.firstPayment ? Number(original.firstPayment) : null)
                    : laterPaymentAmount(slot);
                const show =
                    index === 0 ||
                    amount !== null ||
                    (index < 3 && !isCompleted);
                if (index > 0 && amount) totalPayments += amount;
                if (!show) return;

                totalY += 0.7;
                const amountText = amount === null ? "-" : NumberFormatter.format(amount);
                currentPage.drawText(slot.invoiceLabel, {
                    x: 370,
                    y: pageHeight - totalY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
                currentPage.drawText(`${currencyPrefix} ${amountText}`, {
                    x: 480,
                    y: pageHeight - totalY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });
            });

            const balance = (totalAmount - original.discount - (original.credit ? original.credit.credit : 0)) - totalPayments;

            if (balance > 0) {
                totalY += 0.7;
                currentPage.drawText(`BALANCE:`, {
                    x: 370,
                    y: pageHeight - totalY * fontSize,
                    size: standardFontSize,
                    font: timesRomanFont,
                    color: rgb(0, 0, 0),
                });

                currentPage.drawText(
                    `${
                        process.env.REACT_APP_COUNTRY === "SINGAPORE"
                            ? "$"
                            : "RM"
                    } ${NumberFormatter.format(balance)}`,
                    {
                        x: 480,
                        y: pageHeight - totalY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    }
                );
            }
        }

        currentPage.drawLine({
            start: {
                x: 30,
                y: pageHeight - (totalY + 1) * fontSize,
            },
            end: {
                x: 560,
                y: pageHeight - (totalY + 1) * fontSize,
            },
            color: rgb(0, 0, 0),
            opacity: 0.75,
        });

        const paymentTermsLineStep = 0.55;
        const paymentTermsLines = [
            { number: "1.", text: "All cheques should be crossed and made payable to" },
            { text: "Inhome Awning Specialist (Ipoh) Sdn Bhd", font: timesRomanBoldFont },
            { text: principleBusinessDetails.bankAccount, font: timesRomanBoldFont },
            { number: "2.", text: "Goods sold are neither returnable nor refundable" },
        ];
        const paymentTermsBlockLines = paymentTermsLines.length * paymentTermsLineStep;
        const qrMaxWidth = 96;
        const qrScale = qrMaxWidth / qrImage.width;
        const qrWidth = qrImage.width * qrScale;
        const qrHeight = qrImage.height * qrScale;
        const qrX = 30;
        const qrGap = 10;

        const drawNotesAndQr = (notesStartY) => {
            let paymentTermsY = notesStartY;
            const paymentTermsBoxX = 30;
            const paymentTermsBoxWidth = 530;
            const paymentTermsTextX = paymentTermsBoxX + 10;
            const paymentTermsNumberedTextX = paymentTermsTextX + 12;
            const paymentTermsBoxTop = paymentTermsY - 0.75;
            const notesBoxBottom = paymentTermsY + paymentTermsBlockLines + 0.4;

            currentPage.drawRectangle({
                x: paymentTermsBoxX,
                y: pageHeight - notesBoxBottom * fontSize,
                width: paymentTermsBoxWidth,
                height: (notesBoxBottom - paymentTermsBoxTop) * fontSize,
                color: rgb(0.965, 0.965, 0.965),
                borderColor: rgb(0.75, 0.75, 0.75),
                borderWidth: 0.5,
            });

            currentPage.drawText("Notes:", {
                x: paymentTermsTextX,
                y: pageHeight - paymentTermsY * fontSize,
                size: standardFontSize,
                font: timesRomanBoldFont,
                color: rgb(0, 0, 0),
            });

            paymentTermsLines.forEach(({ number, text, font = timesRomanFont }) => {
                paymentTermsY += paymentTermsLineStep;
                if (number) {
                    currentPage.drawText(number, {
                        x: paymentTermsTextX,
                        y: pageHeight - paymentTermsY * fontSize,
                        size: standardFontSize,
                        font: timesRomanFont,
                        color: rgb(0, 0, 0),
                    });
                }
                currentPage.drawText(text, {
                    x: paymentTermsNumberedTextX,
                    y: pageHeight - paymentTermsY * fontSize,
                    size: standardFontSize,
                    font,
                    color: rgb(0, 0, 0),
                });
            });

            const qrTopY = pageHeight - notesBoxBottom * fontSize - qrGap;
            currentPage.drawImage(qrImage, {
                x: qrX,
                y: qrTopY - qrHeight,
                width: qrWidth,
                height: qrHeight,
            });

            return (pageHeight - (qrTopY - qrHeight)) / fontSize;
        };

        const notesStartY = totalY + 1.75;
        const estimatedNotesBottom = notesStartY + paymentTermsBlockLines + 0.4;
        const estimatedQrBottom =
            estimatedNotesBottom + (qrGap + qrHeight) / fontSize;
        const qrWouldBeCutOff =
            pageHeight - estimatedQrBottom * fontSize < bottomMargin;

        let paymentTermsBoxBottom;
        let qrBottomUnits;
        if (qrWouldBeCutOff) {
            currentPage = pdfDoc.addPage();
            pageHeight = currentPage.getSize().height;
            hasMultiplePages = true;
            const footerBottom = drawNotesAndQr(2.5);
            paymentTermsBoxBottom = footerBottom;
            qrBottomUnits = footerBottom;
        } else {
            const footerBottom = drawNotesAndQr(notesStartY);
            paymentTermsBoxBottom = footerBottom;
            qrBottomUnits = footerBottom;
        }

        // Crop page to remove blank space when invoice is short (single page only)
        // Content is drawn from top (y=pageHeight) down; blank space is at bottom (y=0)
        if (!hasMultiplePages) {
            const contentBottom = Math.max(paymentTermsBoxBottom, qrBottomUnits);
            const contentHeight = contentBottom * fontSize + bottomMargin;
            const minPageHeight = 400;
            const newPageHeight = Math.min(
                defaultPageHeight,
                Math.max(minPageHeight, contentHeight)
            );
            if (newPageHeight < defaultPageHeight) {
                // Crop bottom blank space: set media box to show only top portion
                const cropY = defaultPageHeight - newPageHeight;
                currentPage.setMediaBox(0, cropY, defaultPageWidth, newPageHeight);
            }
        }
        }

        // Serialize the PDFDocument to bytes (a Uint8Array)
        const pdfBytes = await pdfDoc.save();

        // Trigger the browser to download the PDF document
        download(
            pdfBytes,
            `${isDeliveryOrder ? "Delivery_Order" : original.invoiceId}-${original?.userId?.name}`,
            "application/pdf"
        );
    } catch (err) {
        alert("Failed");
        console.log(err);
    }
};

export default createCustomerInvoice;

export const createCustomerInvoiceMerged = (original) =>
    createCustomerInvoice(original, { mergeLabourIntoLastItem: true });

export const createDeliveryOrder = (original) =>
    createCustomerInvoice(original, {
        mergeLabourIntoLastItem: true,
        isDeliveryOrder: true,
    });

